import { useEffect, useState } from "react";
import { ShieldCheck, CheckCircle2, TrendingDown, Tag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PLANS } from "./PlanChooser";
import { PaymentStep } from "./PaymentStep";
import { api, apiError } from "../../lib/api";

// Promo / referral code: validated and attached to the account before the subscription is created, so the promo coupon goes on
// the very first invoice. The regular renewal price is always spelled out.
export const PromoBox = ({ plan, demo, onApplied }) => {
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!demo) api.promoMine().then((r) => r.promo && setApplied({ code: r.promo.code, title: r.promo.offer?.title, lines: r.promo.offer?.lines || [] })).catch(() => {}); }, [demo]);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await api.promoApply(code.trim(), plan);
      setApplied({ code: code.trim().toUpperCase(), title: r.title, lines: r.lines });
      onApplied?.();
      toast.success("Promo applied");
    } catch (e) { toast.error(apiError(e, "That promo code didn't work")); }
    finally { setBusy(false); }
  };
  if (applied) {
    return (
      <div className="rounded-lg border border-[#3ecf8e]/40 bg-[#3ecf8e]/10 px-3 py-2.5 text-[12px]" data-testid="promo-applied">
        <div className="flex items-center gap-1.5 font-semibold text-[#3ecf8e]"><CheckCircle2 className="h-3.5 w-3.5" /> Promo Applied · {applied.code}</div>
        <div className="mt-1 font-medium text-[#e8e9ec]">{applied.title}</div>
        <ul className="mt-0.5 text-[#c7cbd1]">{applied.lines.map((l) => <li key={l} data-testid="promo-line">{l}</li>)}</ul>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2" data-testid="promo-box">
      <div className="relative flex-1"><Tag className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#6b7079]" />
        <input data-testid="promo-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && apply()} placeholder="Promo Code" disabled={demo} className="field h-9 w-full pl-8 text-[12px] uppercase" /></div>
      <button type="button" data-testid="promo-apply-btn" onClick={apply} disabled={busy || demo || !code.trim()} className="btn h-9 px-3 text-[12px]">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Apply"}</button>
    </div>
  );
};

// Package tab styled like the "selected package" summary: title, price (fits inside), perks.
export const PackageTab = ({ p, active, dimmed, onClick, email }) => {
  const I = p.icon;
  const color = p.accent === "#d4af37" ? "#f0c945" : "#4c9ffe";
  return (
    <button type="button" data-testid={`choose-${p.id}`} onClick={onClick} style={{ "--glass-accent": p.accent }} className={`glass-card w-full px-4 py-3 text-left transition-opacity duration-300 ${active ? "is-active" : ""} ${dimmed ? "opacity-40 saturate-50 hover:opacity-90" : ""}`} data-active={active} data-dimmed={dimmed}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold" style={{ color }}><I className={`h-4 w-4 ${p.id === "yearly" ? "fill-current" : ""}`} /> Xpattern {p.title}</span>
        <span className="flex items-baseline font-mono text-[26px] font-semibold leading-none tracking-tight drop-shadow-[0_2px_2px_rgba(0,0,0,.6)]">{p.price}<span className="text-[15px]">{p.cents}</span><span className="ml-1 text-[11px] font-normal text-[#9aa0a8]">/ {p.id === "yearly" ? "yr" : "mo"}</span></span>
      </div>
      <div className="mt-0.5 text-[11px] text-[#9aa0a8]">{p.sub}</div>
      {p.compare && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-[#3ecf8e]/35 bg-[#3ecf8e]/12 px-2.5 py-1.5 text-[11px] shadow-[inset_0_1px_0_rgba(255,255,255,.08)]" data-testid={`compare-${p.id}`}>
          <span className="text-[#9fe8c7]">{p.compare.others}</span>
          <span className="flex items-center gap-1 whitespace-nowrap font-semibold text-[#3ecf8e]"><TrendingDown className="h-3.5 w-3.5" /> {p.compare.save}</span>
        </div>
      )}
      <ul className="mt-2 space-y-1 text-[11px] text-[#b4b9c1]">{p.perks.map((t) => <li key={t} className="flex items-start gap-1.5"><CheckCircle2 className="mt-0.5 h-3 w-3 flex-shrink-0 text-[#3ecf8e]" />{t}</li>)}</ul>
      {active && email && <p className="mt-2 flex items-center gap-1 text-[10px] text-[#6b7079]"><ShieldCheck className="h-3 w-3" /> Billed to {email}. Cancel anytime from My Dashboard.</p>}
    </button>
  );
};

// Two-column checkout: packages on the left, card details on the right. Shared by the payment gate and the in-app Buy Subscription popup.
// `only` restricts the package list (e.g. "yearly" for the Monthly → Yearly upgrade).
export const CheckoutPanel = ({ plan, setPlan, onPaid, demo, email, only }) => {
  const [promoNonce, setPromoNonce] = useState(0);
  return (
  <div className="grid gap-6 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]" data-testid="payment-gate">
    <div className="space-y-3" data-testid="gate-plan-options">
      <div className="text-[11px] font-medium uppercase tracking-wide text-[#9aa0a8]">Your package</div>
      {PLANS.filter((p) => p.id !== "trial" && (!only || p.id === only)).map((p) => <PackageTab key={p.id} p={p} active={plan === p.id} dimmed={Boolean(plan) && plan !== p.id} onClick={() => setPlan(p.id)} email={email} />)}
      {!only && <PromoBox plan={plan} demo={demo} onApplied={() => setPromoNonce((n) => n + 1)} />}
      {only === "yearly" && <p className="px-1 text-[11px] text-[#9aa0a8]" data-testid="upgrade-note">Your Monthly plan is replaced today — the Yearly plan starts the moment your payment goes through.</p>}
    </div>
    <div className="glass-panel p-4" data-testid="gate-card-panel">
      <div className="mb-3 text-[11px] font-medium uppercase tracking-wide text-[#9aa0a8]">Payment information</div>
      <PaymentStep key={`${plan}-${promoNonce}`} plan={plan} onPaid={onPaid} demo={demo} hideSummary />
    </div>
  </div>
  );
};
